import { supabase } from "@/integrations/supabase/client";

export interface TemplateShift {
  day_of_week: number; // 0=Monday, 6=Sunday
  shift_code: string;
  split_segments?: Array<{
    label?: string;
    start_time: string;
    end_time: string;
  }> | null;
}

export interface TemplateWeek {
  week_number: number;
  shifts: TemplateShift[];
}

export interface TemplateInput {
  name: string;
  description?: string;
  num_weeks: number;
  weeks: TemplateWeek[];
}

export interface ShiftTemplate {
  id: string;
  company_id: string;
  name: string;
  description: string | null;
  num_weeks: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  usage_count: number;
  last_used_at: string | null;
  weeks?: TemplateWeekWithShifts[];
}

export interface TemplateWeekWithShifts {
  id: string;
  template_id: string;
  week_number: number;
  created_at: string;
  shifts: TemplateShiftRecord[];
}

export interface TemplateShiftRecord {
  id: string;
  template_week_id: string;
  day_of_week: number;
  shift_code: string;
  split_segments: any | null;
  created_at: string;
}

export interface ApplyTemplateInput {
  template_id: string;
  profile_ids: string[];
  year: number;
  month: number;
  overwrite_existing: boolean;
}

export interface TemplateResponse {
  success: boolean;
  template_id?: string;
  created_count?: number;
  error_message?: string | null;
}

/**
 * Fetch all templates for the current company
 */
export const fetchCompanyTemplates = async (): Promise<ShiftTemplate[]> => {
  const { data, error } = await supabase.rpc('fetch_company_templates');

  if (error) {
    console.error('Error fetching templates:', error);
    throw new Error(error.message || 'Failed to fetch templates');
  }

  return (data as unknown as ShiftTemplate[]) || [];
};

/**
 * Create a new shift template
 */
export const createTemplate = async (input: TemplateInput): Promise<string> => {
  const { data, error } = await supabase.rpc('create_shift_template', {
    p_name: input.name,
    p_description: input.description || null,
    p_num_weeks: input.num_weeks,
    p_weeks: input.weeks as unknown as any
  });

  if (error) {
    console.error('Error creating template:', error);
    throw new Error(error.message || 'Failed to create template');
  }

  const result = data?.[0] as TemplateResponse;
  
  if (!result?.success || !result.template_id) {
    throw new Error(result?.error_message || 'Failed to create template');
  }

  return result.template_id;
};

/**
 * Update an existing template
 */
export const updateTemplate = async (
  templateId: string,
  updates: Partial<TemplateInput>
): Promise<void> => {
  const { data, error } = await supabase.rpc('update_shift_template', {
    p_template_id: templateId,
    p_name: updates.name,
    p_description: updates.description || null,
    p_num_weeks: updates.num_weeks,
    p_weeks: updates.weeks as unknown as any
  });

  if (error) {
    console.error('Error updating template:', error);
    throw new Error(error.message || 'Failed to update template');
  }

  const result = data?.[0] as TemplateResponse;
  
  if (!result?.success) {
    throw new Error(result?.error_message || 'Failed to update template');
  }
};

/**
 * Delete a template (soft delete)
 */
export const deleteTemplate = async (templateId: string): Promise<void> => {
  const { data, error } = await supabase.rpc('delete_shift_template', {
    p_template_id: templateId
  });

  if (error) {
    console.error('Error deleting template:', error);
    throw new Error(error.message || 'Failed to delete template');
  }

  const result = data?.[0] as TemplateResponse;
  
  if (!result?.success) {
    throw new Error(result?.error_message || 'Failed to delete template');
  }
};

/**
 * Apply a template to selected employees
 */
export const applyTemplate = async (input: ApplyTemplateInput): Promise<number> => {
  const { data, error } = await (supabase.rpc as any)('apply_template_to_roster', {
    p_template_id: input.template_id,
    p_profile_ids: input.profile_ids,
    p_year: input.year,
    p_month: input.month,
    p_overwrite: input.overwrite_existing
  });

  if (error) {
    console.error('Error applying template:', error);
    throw new Error(error.message || 'Failed to apply template');
  }

  const result = data?.[0] as TemplateResponse;
  
  if (!result?.success) {
    throw new Error(result?.error_message || 'Failed to apply template');
  }

  return result.created_count || 0;
};

/**
 * Get template preview for a given month
 */
export const getTemplatePreview = async (
  templateId: string,
  year: number,
  month: number
) => {
  const { data, error } = await (supabase.rpc as any)('get_template_preview', {
    p_template_id: templateId,
    p_year: year,
    p_month: month
  });

  if (error) {
    console.error('Error getting template preview:', error);
    throw new Error(error.message || 'Failed to get template preview');
  }

  return data;
};
